import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'if_auth_token';

// Configure axios to include auth header if token exists
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      // Clear invalid token
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password, name, captchaToken = '') => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/signup`,
        { email, password, name, captcha_token: captchaToken },
        { withCredentials: true }
      );
      
      // Store token in localStorage as backup
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
      }
      
      setUser(response.data.user);
      setLoading(false);  // Ensure loading state is cleared for redirect
      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      setLoading(false);  // Clear loading on error too
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed. Please try again.' 
      };
    }
  };

  // Helper function to claim guest sessions after login
  const claimGuestSessions = async () => {
    try {
      // Get any guest session IDs from localStorage
      const guestSessionIds = JSON.parse(localStorage.getItem('guest_session_ids') || '[]');
      
      await axios.post(
        `${BACKEND_URL}/api/auth/claim-guest-sessions`,
        { guest_session_ids: guestSessionIds },
        { withCredentials: true }
      );
      
      // Clear the guest sessions from localStorage after claiming
      localStorage.removeItem('guest_session_ids');
      console.log('Guest sessions claimed successfully');
    } catch (error) {
      // Don't throw - this is a best-effort operation
      console.log('No guest sessions to claim or claim failed:', error.message);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Store token in localStorage as backup
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
      }
      
      setUser(response.data.user);
      setLoading(false);  // Ensure loading state is cleared for redirect
      
      // Claim any guest sessions after successful login
      await claimGuestSessions();
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);  // Clear loading on error too
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed. Please check your credentials.' 
      };
    }
  };

  const loginWithGoogle = async (sessionId) => {
    try {
      const savedRole = localStorage.getItem('signup_user_role') || 'innovator';
      localStorage.removeItem('signup_user_role');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/google`,
        { session_id: sessionId, user_role: savedRole },
        { withCredentials: true }
      );
      
      // Store token in localStorage as backup
      if (response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
      }
      
      setUser(response.data.user);
      setLoading(false);  // Ensure loading state is cleared for redirect
      
      // Claim any guest sessions after successful Google login
      await claimGuestSessions();
      
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      setLoading(false);  // Clear loading on error too
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Google login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state and storage
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    }
  };

  const canAccessFeature = (feature) => {
    if (!user) return false;
    
    const tier = user.subscription_tier;
    
    switch (feature) {
      case 'unlimited_sessions':
        return tier === 'pro' || tier === 'teams' || tier === 'academic';
      case 'ai_projections':
        return tier === 'pro' || tier === 'teams' || tier === 'academic';
      case 'multi_persona':
        return tier === 'pro' || tier === 'teams' || tier === 'academic';
      case 'team_collaboration':
        return tier === 'teams' || tier === 'academic';
      case 'challenges':
        return true; // All users can access challenges
      default:
        return false;
    }
  };

  // Refresh user data from server
  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    setUser,  // Expose setUser for magic link verification
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    checkAuth,
    refreshUser,
    canAccessFeature,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
